import { useState, useCallback, useMemo } from "react";
import { IEvent } from "../../interfaces/models/IEvent";

export interface ValidationErrors {
  [key: string]: string | undefined;
}

interface FieldRule {
  required?: boolean;
  maxLength?: number;
  condition?: (event: IEvent) => boolean;
  customValidator?: (value: any, event: IEvent) => string | undefined;
}

interface ValidationConfig {
  [field: string]: FieldRule;
}

const FIELD_LABELS: Record<string, string> = {
  setupType: "Setup Type",
  title: "Event Title",
  description: "Event Description",
  overviewTabDescription: "Overview Tab Description",
  startDate: "Start Date",
  timeZone: "Timezone",
  location: "Location",
  countryName: "Country",
  cityName: "City",
  sponsorHost: "Sponsor Host",
};

const SPONSOR_HOST_TYPE_MAP: Record<string, { types: string[]; name: string }> = {
  member: { types: ['member', 'wsgMember', 'groupMember'], name: 'Member Sponsor' },
  group: { types: ['group', 'groupMember'], name: 'WSG Group' },
  guest: { types: ['wsgGuest', 'guest'], name: 'Guest Sponsor' },
  industry: { types: ['industry'], name: 'Industry' },
};

const REQUIRED_SUFFIX = ' is required';

const getFieldLabel = (fieldName: string): string => {
  return FIELD_LABELS[fieldName] || fieldName;
};

const ERROR_MESSAGES = {
  REQUIRED: (field: string) => getFieldLabel(field),
  MAX_LENGTH: (field: string, max: number) => `${getFieldLabel(field)} cannot exceed ${max} characters`,
  START_DATE: () => getFieldLabel("startDate"),
  TIMEZONE: () => getFieldLabel("timeZone"),
  LOCATION: () => getFieldLabel("location"),
  COUNTRY: () => getFieldLabel("countryName"),
  CITY: () => getFieldLabel("cityName"),
  SPONSOR_HOST_REQUIRED: (type: string) => `At least one ${type} is required`,
};

const getFieldValue = (event: IEvent, field: string): any => {
  if (field.includes('.')) {
    const [parent, child] = field.split('.');
    const parentValue = event[parent as keyof IEvent];
    if (parentValue && typeof parentValue === 'object' && 'countryName' in parentValue) {
      return (parentValue as any)[child];
    }
    return undefined;
  }
  return event[field as keyof IEvent];
};

const isEmptyValue = (value: any): boolean => {
  return value === null || value === undefined || value === '' || 
         (typeof value === 'string' && value.trim() === '');
};

const validators = {
  required: (value: any, fieldName: string): string | undefined => {
    if (isEmptyValue(value)) {
      return `${ERROR_MESSAGES.REQUIRED(fieldName)}${REQUIRED_SUFFIX}`;
    }
    return undefined;
  },

  maxLength: (value: any, max: number, fieldName: string): string | undefined => {
    if (value && typeof value === 'string' && value.length > max) {
      return ERROR_MESSAGES.MAX_LENGTH(fieldName, max);
    }
    return undefined;
  },

  dateFields: (event: IEvent, field: string, value: any): Record<string, string | undefined> => {
    const errors: Record<string, string | undefined> = {};
    const comingSoon = field === 'comingSoonDate' ? value : event.comingSoonDate;
    
    if (!comingSoon) {
      const startDate = field === 'startDate' ? value : event.startDate;
      
      if (!startDate && field !== 'comingSoonDate') {
        errors.startDate = `${ERROR_MESSAGES.START_DATE()}${REQUIRED_SUFFIX}`;
      }
    }
    
    return errors;
  },

  locationFields: (event: IEvent, field: string, value: any): Record<string, string | undefined> => {
    const errors: Record<string, string | undefined> = {};
    
    if (event.eventType === 'virtual') {
      return errors;
    }
    
    const comingSoon = field === 'comingSoonLocation' ? value : event.comingSoonLocation;
    
    if (!comingSoon) {
      const address = event.address || {};
      const countryName = field === 'countryName' ? value : address.countryName;
      const cityName = field === 'cityName' ? value : address.cityName;
      
      if (isEmptyValue(countryName) && field !== 'comingSoonLocation') {
        errors.countryName = `${ERROR_MESSAGES.COUNTRY()}${REQUIRED_SUFFIX}`;
      }
      if (isEmptyValue(cityName) && field !== 'comingSoonLocation') {
        errors.cityName = `${ERROR_MESSAGES.CITY()}${REQUIRED_SUFFIX}`;
      }
    }
    
    return errors;
  },

  sponsorHost: (event: IEvent): string | undefined => {
    const sponsorHost = event?.sponsorHost || [];
    const hostType = event?.eventHostSponsor;
    
    if (!hostType) {
      return undefined;
    }
    
    const typeMapping: Record<string, string> = {
      'member': 'member',
      'group': 'wsg_group',
      'guest': 'guest sponsor',
      'industry': 'industry',
    };
    
    const requiredTypes: string[] = [];
    
    for (const [key, config] of Object.entries(SPONSOR_HOST_TYPE_MAP)) {
      if (config.types.includes(hostType)) {
        requiredTypes.push(key);
      }
    }
    
    for (const key of requiredTypes) {
      const config = SPONSOR_HOST_TYPE_MAP[key];
      const sponsorType = typeMapping[key];
      const hasRequired = sponsorHost.some(s => s.type === sponsorType);
      if (!hasRequired) {
        return ERROR_MESSAGES.SPONSOR_HOST_REQUIRED(config.name);
      }
    }
    
    return undefined;
  },
};

const getValidationConfig = (isEditMode: boolean): ValidationConfig => {
  const config: ValidationConfig = {
    setupType: { required: true },
    title: { required: true },
    description: { maxLength: 1000 },
    overviewTabDescription: { maxLength: 3000 },
  };

  if (isEditMode) {
    config.startDate = {
      required: true,
      condition: (event) => !event.comingSoonDate,
    };
    config.sponsorHost = {
      customValidator: validators.sponsorHost,
    };
  }

  return config;
};

const DATE_FIELDS = ['startDate', 'comingSoonDate', 'timeZone'];
const LOCATION_FIELDS = ['location', 'comingSoonLocation', 'countryName', 'cityName'];

export function useEventValidation(rules: ValidationConfig = {}, isEditMode: boolean = false) {
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const config = useMemo(() => {
    const defaultConfig = getValidationConfig(isEditMode);
    return { ...defaultConfig, ...rules };
  }, [isEditMode, rules]);

  const validateField = useCallback(
    (field: keyof ValidationErrors, value: any, event?: IEvent): ValidationErrors => {
      const newErrors: ValidationErrors = {};
      
      if (!event) {
        return newErrors;
      }

      const rule = config[field as string];

      if (rule) {
        if (rule.required) {
          const shouldValidate = !rule.condition || rule.condition(event);
          if (shouldValidate) {
            const error = validators.required(value, field as string);
            if (error) {
              newErrors[field] = error;
            }
          }
        }

        if (rule.maxLength && value) {
          const error = validators.maxLength(value, rule.maxLength, field as string);
          if (error) {
            newErrors[field] = error;
          }
        }

        if (rule.customValidator) {
          const error = rule.customValidator(value, event);
          if (error) {
            newErrors[field] = error;
          }
        }
      }

      if (isEditMode) {
        if (DATE_FIELDS.includes(field as string)) {
          Object.assign(newErrors, validators.dateFields(event, field as string, value));
        }

        if (LOCATION_FIELDS.includes(field as string)) {
          Object.assign(newErrors, validators.locationFields(event, field as string, value));
        }

        if (field === 'sponsorHost' || field === 'eventHostSponsor') {
          const error = validators.sponsorHost(event);
          if (error) {
            newErrors.sponsorHost = error;
          } else {
            delete newErrors.sponsorHost;
          }
        }
      }

      return newErrors;
    },
    [config, isEditMode]
  );

  const validate = useCallback(
    (event: IEvent): { isValid: boolean; errors: ValidationErrors } => {
      const newErrors: ValidationErrors = {};

      for (const [field, rule] of Object.entries(config)) {
        const shouldValidate = !rule.condition || rule.condition(event);
        
        if (!shouldValidate) {
          continue;
        }

        const value = getFieldValue(event, field);
        const fieldErrors = validateField(field as keyof ValidationErrors, value, event);
        Object.assign(newErrors, fieldErrors);
      }

      if (isEditMode) {
        if (!event.comingSoonDate) {
          if (!event.startDate) {
            newErrors.startDate = `${ERROR_MESSAGES.START_DATE()}${REQUIRED_SUFFIX}`;
          }
        }

        if (!event.comingSoonLocation && event.eventType !== 'virtual') {
          if (isEmptyValue(event.address?.countryName)) {
            newErrors.countryName = `${ERROR_MESSAGES.COUNTRY()}${REQUIRED_SUFFIX}`;
          }
          if (isEmptyValue(event.address?.cityName)) {
            newErrors.cityName = `${ERROR_MESSAGES.CITY()}${REQUIRED_SUFFIX}`;
          }
        }

        const sponsorError = validators.sponsorHost(event);
        if (sponsorError) {
          newErrors.sponsorHost = sponsorError;
        }
      }

      setErrors(newErrors);
      return {
        isValid: Object.keys(newErrors).length === 0,
        errors: newErrors,
      };
    },
    [config, isEditMode, validateField]
  );

  const validateFieldCallback = useCallback(
    (field: keyof ValidationErrors, value: any, event?: IEvent) => {
      if (!hasAttemptedSave) {
        return;
      }

      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        const fieldErrors = validateField(field, value, event);
        
        for (const [errorField, errorMessage] of Object.entries(fieldErrors)) {
          if (errorMessage) {
            newErrors[errorField] = errorMessage;
          } else {
            delete newErrors[errorField];
          }
        }

        if (!fieldErrors[field as string] && newErrors[field]) {
          delete newErrors[field];
        }

        return newErrors;
      });
    },
    [hasAttemptedSave, validateField]
  );

  const markAsAttempted = useCallback(() => {
    setHasAttemptedSave(true);
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
    setHasAttemptedSave(false);
  }, []);

  const hasErrors = useMemo(() => {
    return Object.keys(errors).length > 0;
  }, [errors]);

  return {
    validate,
    validateField: validateFieldCallback,
    markAsAttempted,
    clearErrors,
    errors,
    hasErrors,
    hasAttemptedSave,
  };
}
